import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  Autocomplete,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  People as PeopleIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import Editor from "@monaco-editor/react";
import AdminPage from "./AdminPage";
import { apiClient } from "../utils/apiClient";
import type {
  PermissionGroup,
  PermissionDescriptionsResponse,
} from "@services/permissions/types";

interface UserOption {
  id: string;
  email: string;
  name: string;
}

interface OrganizationOption {
  id: string;
  name: string;
}

export function AdminPermissionGroupsPage() {
  const [groups, setGroups] = useState<PermissionGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [usersDialogOpen, setUsersDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  // Organizations
  const [organizations, setOrganizations] = useState<OrganizationOption[]>([]);

  // Form states
  const [newGroupName, setNewGroupName] = useState("");
  const [newGroupDescription, setNewGroupDescription] = useState("");
  const [newGroupOrgId, setNewGroupOrgId] = useState("");
  const [editGroupName, setEditGroupName] = useState("");
  const [editGroupDescription, setEditGroupDescription] = useState("");
  const [editPermissionsJson, setEditPermissionsJson] = useState("");
  const [selectedGroup, setSelectedGroup] = useState<PermissionGroup | null>(
    null
  );

  // Permission descriptions
  const [permissionDescriptions, setPermissionDescriptions] =
    useState<PermissionDescriptionsResponse["permissions"]>([]);

  // Users dialog states
  const [allUsers, setAllUsers] = useState<UserOption[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [addUserValue, setAddUserValue] = useState<UserOption | null>(null);

  useEffect(() => {
    fetchGroups();
    fetchPermissionDescriptions();
    fetchOrganizations();
  }, []);

  const fetchGroups = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await apiClient.post<{ groups: PermissionGroup[] }>(
        `/admin/permissions/list`,
        {}
      );
      setGroups(res.groups);
    } catch (err) {
      console.error("Error fetching permission groups:", err);
      setError("Failed to load permission groups");
    } finally {
      setLoading(false);
    }
  };

  const fetchPermissionDescriptions = async () => {
    try {
      const res = await apiClient.get<PermissionDescriptionsResponse>(
        `/admin/permissions/permission-descriptions`
      );
      setPermissionDescriptions(res.permissions);
    } catch {
      // non-critical
    }
  };

  const fetchOrganizations = async () => {
    try {
      const res = await apiClient.post<{ organizations: OrganizationOption[] }>(
        `/admin/organizations/list`,
        {}
      );
      setOrganizations(res.organizations);
    } catch {
      // non-critical
    }
  };

  const fetchAllUsers = async () => {
    try {
      setLoadingUsers(true);
      const res = await apiClient.post<{
        users: { id: string; email: string; name: string }[];
      }>(`/admin/list_users`, {});
      setAllUsers(
        res.users.map((u) => ({ id: u.id, email: u.email, name: u.name }))
      );
    } catch {
      // non-critical
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleCreate = async () => {
    try {
      setError(null);
      const res = await apiClient.post<{ group: PermissionGroup }>(
        `/admin/permissions/create`,
        {
          name: newGroupName.trim(),
          description: newGroupDescription.trim() || undefined,
          orgId: newGroupOrgId,
        }
      );
      setGroups([res.group, ...groups]);
      setCreateDialogOpen(false);
      setNewGroupName("");
      setNewGroupDescription("");
      setNewGroupOrgId("");
    } catch (err: any) {
      setError(err.message || "Failed to create group");
    }
  };

  const handleUpdate = async () => {
    if (!selectedGroup) return;

    try {
      setError(null);

      // Validate JSON
      let permissions: Record<string, any> | undefined;
      if (editPermissionsJson.trim()) {
        try {
          permissions = JSON.parse(editPermissionsJson);
        } catch {
          setError("Invalid JSON in permissions");
          return;
        }
      }

      const res = await apiClient.put<{ group: PermissionGroup }>(
        `/admin/permissions/update`,
        {
          id: selectedGroup.id,
          name: editGroupName.trim() || undefined,
          description: editGroupDescription.trim() || null,
          permissions,
        }
      );
      setGroups(
        groups.map((g) => (g.id === selectedGroup.id ? res.group : g))
      );
      setEditDialogOpen(false);
      setSelectedGroup(null);
    } catch (err: any) {
      setError(err.message || "Failed to update group");
    }
  };

  const handleDelete = async () => {
    if (!selectedGroup) return;

    try {
      setError(null);
      await apiClient.post(`/admin/permissions/delete`, {
        id: selectedGroup.id,
      });
      setGroups(groups.filter((g) => g.id !== selectedGroup.id));
      setDeleteConfirmOpen(false);
      setSelectedGroup(null);
    } catch (err: any) {
      setError(err.message || "Failed to delete group");
    }
  };

  const handleAddUser = async (user: UserOption) => {
    if (!selectedGroup) return;

    try {
      setError(null);
      await apiClient.post(`/admin/permissions/add-user`, {
        userId: user.id,
        permissionGroupId: selectedGroup.id,
      });
      // Refresh groups to get updated user list
      const res = await apiClient.post<{ groups: PermissionGroup[] }>(
        `/admin/permissions/list`,
        {}
      );
      setGroups(res.groups);
      const updated = res.groups.find((g) => g.id === selectedGroup.id);
      if (updated) setSelectedGroup(updated);
      setAddUserValue(null);
    } catch (err: any) {
      setError(err.message || "Failed to add user");
    }
  };

  const handleRemoveUser = async (userId: string) => {
    if (!selectedGroup) return;

    try {
      setError(null);
      await apiClient.post(`/admin/permissions/remove-user`, {
        userId,
        permissionGroupId: selectedGroup.id,
      });
      const res = await apiClient.post<{ groups: PermissionGroup[] }>(
        `/admin/permissions/list`,
        {}
      );
      setGroups(res.groups);
      const updated = res.groups.find((g) => g.id === selectedGroup.id);
      if (updated) setSelectedGroup(updated);
    } catch (err: any) {
      setError(err.message || "Failed to remove user");
    }
  };

  const openEditDialog = (group: PermissionGroup) => {
    setSelectedGroup(group);
    setEditGroupName(group.name);
    setEditGroupDescription(group.description || "");
    setEditPermissionsJson(JSON.stringify(group.permissions, null, 2));
    setEditDialogOpen(true);
  };

  const openUsersDialog = (group: PermissionGroup) => {
    setSelectedGroup(group);
    setUsersDialogOpen(true);
    fetchAllUsers();
  };

  const openDeleteDialog = (group: PermissionGroup) => {
    setSelectedGroup(group);
    setDeleteConfirmOpen(true);
  };

  if (loading) {
    return (
      <AdminPage>
        <Box
          sx={{ display: "flex", justifyContent: "center", padding: "40px" }}
        >
          <CircularProgress />
        </Box>
      </AdminPage>
    );
  }

  return (
    <AdminPage>
      <Box sx={{ padding: "20px" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            marginBottom: "16px",
          }}
        >
          <Typography variant="h4" sx={{ flexGrow: 1 }}>
            Permission Groups
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Create Group
          </Button>
        </Box>

        {error && (
          <Alert
            severity="error"
            sx={{ marginBottom: "16px" }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <strong>Name</strong>
                </TableCell>
                <TableCell>
                  <strong>Organization</strong>
                </TableCell>
                <TableCell>
                  <strong>Description</strong>
                </TableCell>
                <TableCell align="center">
                  <strong>Users</strong>
                </TableCell>
                <TableCell>
                  <strong>Permissions</strong>
                </TableCell>
                <TableCell align="center">
                  <strong>Actions</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No permission groups found
                  </TableCell>
                </TableRow>
              ) : (
                groups.map((group) => (
                  <TableRow key={group.id}>
                    <TableCell
                      sx={{
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                      }}
                    >
                      {group.name}
                    </TableCell>
                    <TableCell>
                      {group.orgName || "-"}
                    </TableCell>
                    <TableCell
                      sx={{
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                        maxWidth: 200,
                      }}
                    >
                      {group.description || "-"}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={group.users.length}
                        size="small"
                        color={group.users.length > 0 ? "primary" : "default"}
                      />
                    </TableCell>
                    <TableCell>
                      {Object.entries(group.permissions || {}).map(
                        ([key, val]) => (
                          <Chip
                            key={key}
                            label={`${key}: ${val}`}
                            size="small"
                            sx={{ mr: 0.5, mb: 0.5 }}
                            color={val === true ? "success" : "default"}
                          />
                        )
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => openUsersDialog(group)}
                        title="Manage users"
                      >
                        <PeopleIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => openEditDialog(group)}
                        title="Edit group"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => openDeleteDialog(group)}
                        title="Delete group"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Create Dialog */}
        <Dialog
          open={createDialogOpen}
          onClose={() => setCreateDialogOpen(false)}
        >
          <DialogTitle>Create Permission Group</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Group Name"
              fullWidth
              variant="outlined"
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
            />
            <FormControl fullWidth margin="dense">
              <InputLabel>Organization</InputLabel>
              <Select
                value={newGroupOrgId}
                onChange={(e) => setNewGroupOrgId(e.target.value as string)}
                label="Organization"
              >
                {organizations.map((org) => (
                  <MenuItem key={org.id} value={org.id}>
                    {org.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              margin="dense"
              label="Description"
              fullWidth
              variant="outlined"
              value={newGroupDescription}
              onChange={(e) => setNewGroupDescription(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleCreate}
              variant="contained"
              disabled={!newGroupName.trim() || !newGroupOrgId}
            >
              Create
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Dialog with Monaco Editor */}
        <Dialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Edit Permission Group</DialogTitle>
          <DialogContent>
            <TextField
              margin="dense"
              label="Group Name"
              fullWidth
              variant="outlined"
              value={editGroupName}
              onChange={(e) => setEditGroupName(e.target.value)}
            />
            <TextField
              margin="dense"
              label="Description"
              fullWidth
              variant="outlined"
              value={editGroupDescription}
              onChange={(e) => setEditGroupDescription(e.target.value)}
              sx={{ mb: 2 }}
            />

            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Permissions (JSON)
            </Typography>

            {/* Permission descriptions reference */}
            {permissionDescriptions.length > 0 && (
              <Box
                sx={{
                  mb: 1,
                  p: 1,
                  bgcolor: "grey.50",
                  borderRadius: 1,
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontWeight: 600 }}
                >
                  Available permissions:
                </Typography>
                {permissionDescriptions.map((p) => (
                  <Typography
                    key={p.key}
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block" }}
                  >
                    <strong>{p.key}</strong> ({p.type}): {p.description}
                  </Typography>
                ))}
              </Box>
            )}

            <Box
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                overflow: "hidden",
              }}
            >
              <Editor
                height="200px"
                defaultLanguage="json"
                value={editPermissionsJson}
                onChange={(value) => setEditPermissionsJson(value || "")}
                options={{
                  minimap: { enabled: false },
                  lineNumbers: "off",
                  scrollBeyondLastLine: false,
                  fontSize: 13,
                  tabSize: 2,
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleUpdate} variant="contained">
              Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteConfirmOpen}
          onClose={() => setDeleteConfirmOpen(false)}
        >
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete the group "
              {selectedGroup?.name}"?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
            <Button onClick={handleDelete} variant="contained" color="error">
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Users Dialog */}
        <Dialog
          open={usersDialogOpen}
          onClose={() => setUsersDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            Users in {selectedGroup?.name}
          </DialogTitle>
          <DialogContent>
            {/* Add user */}
            <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
              <Autocomplete
                size="small"
                sx={{ flexGrow: 1 }}
                options={allUsers.filter(
                  (u) =>
                    !selectedGroup?.users.some(
                      (gu) => gu.id === u.id
                    )
                )}
                getOptionLabel={(o) => `${o.email} (${o.name})`}
                value={addUserValue}
                onChange={(_, val) => setAddUserValue(val)}
                loading={loadingUsers}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Add user..." />
                )}
              />
              <Button
                variant="contained"
                size="small"
                disabled={!addUserValue}
                onClick={() => addUserValue && handleAddUser(addUserValue)}
              >
                Add
              </Button>
            </Box>

            {/* User list */}
            {selectedGroup?.users.length === 0 ? (
              <Typography
                align="center"
                color="text.secondary"
                sx={{ py: 2 }}
              >
                No users in this group
              </Typography>
            ) : (
              selectedGroup?.users.map((user) => (
                <Box
                  key={user.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    py: 0.5,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Box>
                    <Typography variant="body2">{user.email}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {user.name}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveUser(user.id)}
                    title="Remove user"
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setUsersDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AdminPage>
  );
}
